// ─────────────────────────────────────────────────────────────────────────
// MUI theme bridge. MUI is used for BEHAVIOR/A11Y only (Dialog, Menu, Tabs,
// TextField, Tooltip, Snackbar, Drawer, Slider, Switch, Skeleton). Its theme
// is aligned to our design tokens so MUI components inherit the PCS look
// without us forking their internals. Visual components (gauges, charts,
// cards) are custom CSS/SVG driven by the CSS variables in variables.css.
//
// MUI needs concrete color values (it derives hover/contrast shades), so the
// brand hex values are mirrored here. They MUST match variables.css.
// ─────────────────────────────────────────────────────────────────────────
import { createTheme } from '@mui/material/styles';

// Brand values — keep in sync with src/styles/variables.css.
const BRAND = {
  blue: '#0077CC',
  navy: '#002266',
  green: '#119944',
  yellow: '#FFBB11',
  orange: '#FF6600',
  purple: '#552299',
  red: '#CC0000',
};

const COMMON = {
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 10 }, // matches --r
  // Disable MUI's CssBaseline-style resets bleeding into our tokens; we own base.css.
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: false } },
    MuiTooltip: { defaultProps: { arrow: true } },
  },
};

const lightPalette = {
  mode: 'light',
  primary: { main: BRAND.blue, dark: BRAND.navy, contrastText: '#FFFFFF' },
  secondary: { main: BRAND.purple, contrastText: '#FFFFFF' },
  success: { main: BRAND.green },
  warning: { main: BRAND.yellow },
  error: { main: BRAND.red },
  info: { main: BRAND.blue },
  background: { default: '#F9F8F6', paper: '#FFFFFF' },
  text: { primary: '#222233', secondary: '#445566' },
  divider: '#E8E7E3',
};

const darkPalette = {
  mode: 'dark',
  primary: { main: '#3B9BE8', dark: BRAND.navy, contrastText: '#12111A' },
  secondary: { main: '#9B6BD6', contrastText: '#12111A' },
  success: { main: '#2FB85F' },
  warning: { main: BRAND.yellow },
  error: { main: '#E04545' },
  info: { main: '#3B9BE8' },
  background: { default: '#12111A', paper: '#1F1E2A' },
  text: { primary: '#ECEBF2', secondary: '#A6A4BC' },
  divider: '#322F44',
};

export const buildTheme = (mode = 'light') =>
  createTheme({ ...COMMON, palette: mode === 'dark' ? darkPalette : lightPalette });

export const lightTheme = buildTheme('light');
export const darkTheme = buildTheme('dark');

export default buildTheme;
